import {
    createRouter,
    createWebHistory,
    RouteRecordRaw,
    RouteLocationNormalized
} from 'vue-router'
import { useAuthStore } from '@stores/auth'
import { getUserRouteFallback, isUserRouteAllowedInMode } from '@utils/productMode'

// 声明路由元数据模块，扩展Vue Router类型
declare module 'vue-router' {
    interface RouteMeta {
        requiresAuth?: boolean;
        requiresAdmin?: boolean;
        requiresStaff?: boolean;
        roles?: string[];
        title?: string;
    }
}

/*
懒加载 :LoginView,HomeView
其它 组件使用 import()懒加载方式
*/
const routes: Array<RouteRecordRaw> = [
    {
        path: '/',
        name: 'home',
        component: () => import('@views/HomeView.vue'),
        meta: { requiresAuth: false }
    },
    {
        path: '/login',
        name: 'login',
        component: () => import('@views/LoginView.vue'),
        meta: { requiresAuth: false }
    },
    {
        path: '/search',
        name: 'search',
        component: () => import('@views/SearchView.vue'),
        meta: {
            requiresAuth: true,
            title: '搜索人才'
        }
    },
    {
        path: '/user-management',
        name: 'userManagement',
        component: () => import('@views/UserManageView.vue'),
        meta: {
            requiresStaff: true,
            title: '用户管理'
        }
    },
    {
        path: '/usersManage',
        redirect: '/user-management',
    },
    {
        path: '/positions',
        name: 'positions',
        component: () => import('@views/PositionsView.vue'),
        meta: {
            requiresAuth: true,
            title: '我的职位'
        }
    },
    {
        path: '/position-management',
        name: 'positionManagement',
        component: () => import('@views/PositionManageView.vue'),
        meta: {
            requiresStaff: true,
            title: '职位管理'
        }
    },
    {
        path: '/positions/toolbox',
        name: 'positionsToolbox',
        component: () => import('@views/PositionsToolboxView.vue'),
        meta: {
            requiresAuth: true,
            title: 'AI职位工具箱'
        }
    },
    {
        path: '/profile-pool-management',
        name: 'profilePoolManagement',
        component: () => import('@views/ProfilePoolManageView.vue'),
        meta: {
            requiresAdmin: true,
            title: '人才池管理'
        }
    },
    {
        path: '/profilePool',
        redirect: '/profile-pool-management',
    },
    {
        path: '/resume/toolbox',
        name: 'resumeToolbox',
        component: () => import('@views/ResumeToolboxView.vue'),
        meta: {
            requiresAuth: true,
            title: 'AI简历工具箱'
        }
    },
    {
        path: '/points-management',
        name: 'pointsManagement',
        component: () => import('@views/PointsManageView.vue'),
        meta: {
            requiresStaff: true,
            title: '积分管理'
        }
    },
    {
        path: '/pointsManage',
        redirect: '/points-management',
    },
    {
        path: '/system-logs-management',
        name: 'systemLogsManagement',
        component: () => import('@views/SystemLogsManageView.vue'),
        meta: {
            requiresAdmin: true,
            title: '日志管理'
        }
    },
    {
        path: '/systemLogs',
        redirect: '/system-logs-management',
    },
    {
        path: '/invitation-management',
        name: 'invitationManagement',
        component: () => import('@views/InvitationManageView.vue'),
        meta: {
            requiresStaff: true,
            title: '邀约管理'
        }
    },
    {
        path: '/favorites',
        name: 'favorites',
        component: () => import('@views/FavoritesView.vue'),
        meta: {
            requiresAuth: true,
            title: '职位收藏的人才'
        }
    },
    {
        path: '/candidates',
        name: 'candidates',
        component: () => import('@views/CandidatesView.vue'),
        meta: {
            requiresAuth: true,
            title: '我的人才'
        }
    },
    {
        path: '/interview-invitations',
        name: 'interviewInvitations',
        component: () => import('@views/InviteInterviewView.vue'),
        meta: {
            requiresAuth: true,
            title: '邀约面试人才'
        }
    },
    {
        path: '/resume-invitations',
        name: 'resumeInvitations',
        component: () => import('@views/InviteResumeView.vue'),
        meta: {
            requiresAuth: true,
            title: '邀约简历人才'
        }
    },
    {
        path: '/points/records',
        name: 'pointsRecords',
        component: () => import('@views/PointsRecordsView.vue'),
        meta: {
            requiresAuth: true,
            title: '积分记录'
        }
    },
    {
        path: '/user/center',
        name: 'userCenter',
        component: () => import('@views/UserCenterView.vue'),
        meta: {
            requiresAuth: true,
            title: '用户中心'
        }
    },
    {
        path: '/settings',
        name: 'settings',
        component: () => import('@views/SettingsView.vue'),
        meta: {
            requiresAuth: true,
            title: '用户设置'
        }
    },
    {
        path: '/help',
        name: 'help',
        component: () => import('@views/HelpView.vue'),
        meta: {
            requiresAuth: true,
            title: '帮助中心'
        }
    },
    {
        path: '/messages',
        name: 'messages',
        component: () => import('@views/MessagesView.vue'),
        meta: {
            requiresAuth: true,
            title: '我的消息'
        }
    },
    {
        path: '/mysearches',
        name: 'mySearches',
        component: () => import('@views/MySearchesView.vue'),
        meta: {
            requiresAuth: true,
            title: '我的搜索'
        }
    },
    {
        path: '/403',
        name: 'accessDenied',
        component: () => import('@views/AccessDeniedView.vue'),
        meta: { title: '无权访问' }
    },
    {
        path: '/:pathMatch(.*)*',
        name: 'notFound',
        component: () => import('@views/NotFoundView.vue'),
        meta: { title: '页面不存在' }
    }
]

const router = createRouter({
    history: createWebHistory(import.meta.env.BASE_URL),
    routes
})

/**
 * 全局导航守卫（扁平化校验）
 *
 * 函数级注释：
 * - 优先处理未登录重定向，避免后续分支嵌套；
 * - 支持 `requiresAdmin` 与 `requiresStaff` 元信息校验；
 * - 支持通用 `roles` 数组校验；
 * - 统一返回 403 以提示无权访问。
 */
router.beforeEach((to: RouteLocationNormalized) => {
    const authStore = useAuthStore()
    const needAuth = to.matched.some(r => r.meta.requiresAuth)

    if (!needAuth) return true
    if (!authStore.isAuthenticated) return { path: '/login', query: { redirect: to.fullPath } }

    if (to.meta.requiresAdmin && !authStore.isAdmin) return { path: '/403' }

    // 后台人员或管理员均可
    if (to.meta.requiresStaff && !(authStore.isStaff || authStore.isAdmin)) return { path: '/403' }

    const roles = to.meta.roles || []
    if (roles.length && !roles.includes(authStore.userRole as string)) return { path: '/403' }

    if (authStore.isUser && !isUserRouteAllowedInMode(to.path)) {
        return { path: getUserRouteFallback() }
    }

    return true
})

export default router
